/* eslint-disable eqeqeq */
import React, { useCallback, useEffect, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { useLocation, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  taxOptions,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import Pagination from "../../components/Pagination";
import { formatSubcontractorName, getModuleNamingBySlug } from "../../lib/Misc";
import MainBody from "../../components/MainBody/MainBody";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import AddEditSubAsset from "./AddEditSubAsset";
import { LucidePlusCircle } from "lucide-react";
import SubAssetActionButton from "./SubAssetActionButton";
import CommonFilters from "../../components/CommonFilters";
import { useTrust } from "../../lib/context/TrustContext";
import { useOrganization } from "../../lib/context/OrganisationContext";
import UploadSubAsset from "./UploadSubAsset";
import Confirmation from "../../components/Confirmation";
import sortCaret from "../../components/SortCaret";
import { cn } from "../../lib/utils";

// Here id => VenderId
const SubAssets = ({ id = "", expandList = false }) => {
  const { organizationData } = useOrganization();

  const navigate = useNavigate();
  const { trustId } = useTrust();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [subAssetLoading, setSubAssetLoading] = useState(false);
  const [subAssetList, setSubAssetList] = useState([]);
  const [page, setPage] = useState(expandList ? 1 : defaultPage || 1);
  const [limit, setLimit] = useState(
    expandList ? defaultLimitPagination : defaultLimit || defaultLimitPagination
  );
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();

  const [searchParam, setSearchParam] = useState(
    expandList ? "" : search || ""
  );
  const [searchParamData, setSearchParamData] = useState(
    expandList ? "" : search || ""
  );
  const [subAssetId, setSubAssetId] = useState(null);

  const [edit, setEdit] = useState(false);
  const [showAddEditSubAsset, setShowAddEditSubAsset] = React.useState(false);

  const [vendorId, setVendorId] = useState();
  const [assetId, setAssetId] = useState();
  const [contactId, setContactId] = useState();

  const [showSubAssetDelete, setShowSubAssetDelete] = useState(false);

  const toggleShowAddEditSubAsset = () => {
    if (showAddEditSubAsset) {
      setEdit(false);
      setSubAssetId(null);
    }
    setShowAddEditSubAsset(!showAddEditSubAsset);
  };

  const toggleShowDelete = () => {
    if (showSubAssetDelete) {
      setSubAssetId(null);
    }
    setShowSubAssetDelete(!showSubAssetDelete);
  };

  var today = new Date();
  today.setHours(0, 0, 0, 0);

  // *API Call for Geting SubAssets - Start
  const fetchSubAssets = useCallback(
    async (passLimit, passPage) => {
      try {
        setSubAssetLoading(true);
        const { data } = await get(
          `${API.GET_SUB_ASSETS}?trust_id=${trustId ? trustId : ""}&vendor_id=${
            vendorId ? vendorId : ""
          }&asset_id=${assetId ? assetId : ""}&limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&contact_id=${contactId ? contactId : ""}&sort_column=${
            sortField ? sortField : ""
          }&sort_order=${sortOrder ? sortOrder : ""}`
        );
        setSubAssetLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const subAssetList = data.data.data;
        setSubAssetList(subAssetList);
        return data.data;
      } catch (error) {
        setSubAssetLoading(false);
        setSubAssetList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      id,
      searchParam,
      sortOrder,
      sortField,
      vendorId,
      assetId,
      contactId,
      trustId,
    ]
  );

  // *API Call for Geting SubAssets - End

  // *SubAssets Table Row and Column Generation, Filtering and Sorting of Record - Start
  const subAssetGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: subAssetList[i]?.id,
        name: subAssetList[i]?.name ? subAssetList[i]?.name : "-",
        description: subAssetList[i]?.description
          ? subAssetList[i]?.description
          : "-",
        identifier: subAssetList[i]?.identifier
          ? subAssetList[i]?.identifier
          : "-",
        vendor: subAssetList[i]?.vendor
          ? formatSubcontractorName(subAssetList[i]?.vendor)
          : "-",
        asset: subAssetList[i]?.asset ? subAssetList[i]?.asset?.name : "-",
        tax_type: subAssetList[i]?.tax_type
          ? taxOptions?.find((item) => item.value === subAssetList[i]?.tax_type)
              ?.label
          : "-",
        tax_percentage:
          subAssetList[i]?.tax_type && subAssetList[i]?.tax_percentage
            ? `${subAssetList[i]?.tax_percentage}%`
            : "-",

        action: (
          <SubAssetActionButton
            id={subAssetList[i]?.id}
            used={subAssetList[i]?.is_used ? true : false}
            toggleShowDelete={toggleShowDelete}
            setEdit={setEdit}
            setSubAssetId={setSubAssetId}
            toggleShowAddEditAsset={toggleShowAddEditSubAsset}
            trustId={trustId}
            subAsset={subAssetList[i]}
            assetId={subAssetList[i]?.asset_id}
          />
        ),
      });
    }
    return items;
  };

  const subAsset = subAssetGenerator(subAssetList?.length);

  const SubAssetColumns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: `${getModuleNamingBySlug(
        organizationData?.organizationData,
        "sub-assets",
        "Sub Asset"
      )} Name`,
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "identifier",
      text: `${getModuleNamingBySlug(
        organizationData?.organizationData,
        "sub-assets",
        "Sub Asset"
      )} Identifier`,
      sort: true,
      sortCaret,
    },
    {
      headerClasses: cn(
        THClasses,
        "whitespace-normal break-words" // fixed width + wrapping
      ),
      classes: cn(
        TDClasses,
        "whitespace-normal break-words" // fixed width + wrapping
      ),
      dataField: "vendor",
      text: `${getModuleNamingBySlug(
        organizationData?.organizationData,
        "vendors",
        "Vendor"
      )}`,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "asset",
      text: `${getModuleNamingBySlug(
        organizationData?.organizationData,
        "assets",
        "Asset"
      )}`,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "tax_type",
      text: "GST/VAT Applicable",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "tax_percentage",
      text: "Tax Percentage (%)",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    fetchSubAssets();
  }, [fetchSubAssets]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchSubAssets(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchSubAssets(limit, page);
  };
  // *SubAssets Table Row and Column Generation, Filtering and Sorting of Record - End

  useEffect(() => {
    if (!expandList) {
      navigate(
        `?limit=${limit ? limit : defaultLimitPagination}&page=${
          page ? page : 1
        }&search=${searchParam ? searchParam : ""}&sort_column=${
          sortField ? sortField : ""
        }&sort_order=${sortOrder ? sortOrder : ""}`,
        {
          replace: true,
        }
      );
    }
  }, [navigate, limit, searchParam, page, sortField, sortOrder, expandList]);

  // Single SubAsset Delete
  const handleSubAssetDelete = async () => {
    try {
      setSubAssetLoading(true);
      await deleteCall(`${API.SUB_ASSETS_DELETE}/${subAssetId}`);
      setSubAssetLoading(false);
      fetchSubAssets(limit, page);
    } catch (error) {
      setSubAssetLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        expandList={expandList}
        mainTitle={getModuleNamingBySlug(
          organizationData?.organizationData,
          "sub-assets",
          "Sub Assets"
        )}
        actionContent={
          <>
            {/* Import SubAsset */}
            <UploadSubAsset
              fetchData={() => fetchSubAssets(limit, page)}
              trustId={trustId}
              ButtonName={"Import"}
              setLoading={setSubAssetLoading}
              organizationData={organizationData}
            />

            {!expandList && (
              <CommonFilters
                trustId={trustId}
                contact={false}
                setContactId={setContactId}
                contactId={contactId}
                vendor={true}
                setVendorId={setVendorId}
                vendorId={vendorId}
                asset={true}
                setAssetId={setAssetId}
                assetId={assetId}
              />
            )}

            <ResponsiveDrawerForm
              description={""}
              title={
                edit
                  ? `Edit ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "sub-assets",
                      "Sub Asset"
                    )}`
                  : `Add ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "sub-assets",
                      "Sub Asset"
                    )}`
              }
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setShowAddEditSubAsset(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>
                    Add{" "}
                    {getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "sub-assets",
                      "Sub Asset"
                    )}
                  </span>
                </div>
              }
              className={""}
              open={showAddEditSubAsset}
              buttonClassName={""}
              setOpen={setShowAddEditSubAsset}
              mobileCancelHidden={true}
              write={true}
            >
              <AddEditSubAsset
                edit={edit}
                toggleShowAddEditSubAsset={toggleShowAddEditSubAsset}
                subAssetID={subAssetId}
                fetchSubAssets={() => fetchSubAssets(limit, page)}
                trustId={trustId}
              />
            </ResponsiveDrawerForm>
          </>
        }
        mainClassName="w-[calc(100%-3rem)]"
        loading={subAssetLoading}
      >
        <div className="custom-table">
          <div className={"table-body-class"}>
            <BootstrapTable
              keyField="subAsset_id"
              remote
              data={subAsset}
              columns={SubAssetColumns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={`Delete ${getModuleNamingBySlug(
          organizationData?.organizationData,
          "sub-assets",
          "Sub Asset"
        )}`}
        open={showSubAssetDelete}
        setOpen={setShowSubAssetDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={`Are you sure you want to delete ${getModuleNamingBySlug(
          organizationData?.organizationData,
          "sub-assets",
          "Sub Asset"
        )?.toLowerCase()}?`}
        okAction={async () => {
          handleSubAssetDelete();
          toggleShowDelete();
          // fetchSubAssets(limit, page);
        }}
        cancelAction={() => {
          setSubAssetId(null);
          setShowSubAssetDelete(!showSubAssetDelete);
        }}
      />
    </>
  );
};
export default SubAssets;
